import { decamelizeKeys } from 'humps';

const GENERIC_PASSENGER_SLOT_TYPES = new Set(['general', 'adult']);

/**
 * Summarizes the category mix and traveler count in one passenger selection.
 * @param {Object} selection - Passenger counts keyed by category.
 * @returns {{hasOnlyGenericSlots: boolean, hasSpecificSlots: boolean, total: number}} Summary.
 */
const summarizePassengerSelection = (selection) => {
  const entries = Object.entries(decamelizeKeys(selection || {})).filter(([, count]) => count > 0);

  return {
    hasOnlyGenericSlots:
      entries.length > 0 &&
      entries.every(([category]) => GENERIC_PASSENGER_SLOT_TYPES.has(category)),
    hasSpecificSlots: entries.some(([category]) => !GENERIC_PASSENGER_SLOT_TYPES.has(category)),
    total: entries.reduce((sum, [, count]) => sum + Number(count), 0),
  };
};

/**
 * Prefer search categories when purchase only preserves generic slots for the same traveler count.
 * @param {Object} purchaseSelection - Passenger counts stored in purchase state.
 * @param {Object} searchSelection - Passenger counts selected during search.
 * @returns {boolean} Whether search categories better represent the selected passenger mix.
 */
const shouldUseSearchPassengersForCategorySlots = (purchaseSelection, searchSelection) => {
  const purchaseSummary = summarizePassengerSelection(purchaseSelection);
  const searchSummary = summarizePassengerSelection(searchSelection);

  if (!searchSummary.total) return false;
  if (!purchaseSummary.total) return true;
  if (purchaseSummary.total !== searchSummary.total) return false;
  if (!purchaseSummary.hasOnlyGenericSlots) return false;
  return searchSummary.hasSpecificSlots;
};

export default shouldUseSearchPassengersForCategorySlots;
